import { useState } from 'react';
import { Link } from 'react-router';
import { api, type ScheduledChangeView } from '../api/client';
import { clockTime, shortWhen } from '../lib/format';
import { displayZone } from '../lib/time';
import { Icon } from '../lib/icons';
import { Button, Card, Chip } from './ui';

/**
 * Changes later, or for a while (1.12.15): what Leland will do at a time, as the person who
 * approved it. Each waiting one can be cancelled or done now; done ones say what happened.
 */

/** "3:11 PM", "tomorrow 8:00 AM" or "Thu, Oct 29, 9:00 AM", in the person's own time zone. */
export function laterText(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const zone = displayZone();
  const day = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const time = clockTime(at);
  if (day(at) === day(now)) return time;
  if (day(at) === day(new Date(now.getTime() + 86_400_000))) return `tomorrow ${time}`;
  return `${new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric' }).format(at)}, ${time}`;
}

/** What it does at its time, in a few words. */
function doing(s: ScheduledChangeView): string {
  return s.action === 'undo' ? 'Put back' : 'Applied';
}

/** One scheduled change, with Cancel and Do it now while it waits. */
function Row({ domain, s, onChanged }: { domain: string; s: ScheduledChangeView; onChanged: (s: ScheduledChangeView) => void }) {
  const [busy, setBusy] = useState<'' | 'cancel' | 'run'>('');
  const [error, setError] = useState('');
  const waiting = s.status === 'waiting' || s.status === 'running';
  const act = async (what: 'cancel' | 'run') => {
    setBusy(what);
    setError('');
    try {
      const r = what === 'cancel' ? await api.cancelScheduled(domain, s.id) : await api.runScheduled(domain, s.id);
      onChanged(r.scheduled);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy('');
    }
  };
  return (
    <li className={`sched-row ${s.status}`}>
      <span className="sched-icon" aria-hidden="true">
        <Icon name={waiting ? 'clock' : s.status === 'done' ? 'check' : s.status === 'failed' ? 'alert' : 'x'} size={16} />
      </span>
      <span className="cell-stack grow">
        <strong>{s.title}</strong>
        <small className="muted">
          {waiting ? (
            <>
              {doing(s)} at <b>{laterText(s.runAt)}</b>
              {s.summary ? ` · ${s.summary}` : ''} · scheduled by {s.userName}
            </>
          ) : (
            <>
              {s.outcome || s.status} · {s.doneAt ? shortWhen(s.doneAt) : ''}
            </>
          )}
        </small>
        {error && (
          <small className="crit-text" role="alert">
            {error}
          </small>
        )}
      </span>
      {waiting ? (
        <span className="sched-actions">
          <Button size="small" variant="secondary" disabled={busy !== '' || s.status === 'running'} onClick={() => void act('run')}>
            {busy === 'run' ? 'Doing it…' : 'Do it now'}
          </Button>
          <Button size="small" variant="ghost" disabled={busy !== '' || s.status === 'running'} onClick={() => void act('cancel')} aria-label={`Cancel: ${s.title}`}>
            Cancel
          </Button>
        </span>
      ) : (
        <Chip small tone={s.status === 'done' ? 'ok' : s.status === 'failed' ? 'warn' : 'neutral'}>
          {s.status === 'done' ? 'Done' : s.status === 'failed' ? 'Not done' : 'Cancelled'}
        </Chip>
      )}
    </li>
  );
}

/** A list of scheduled changes (in Leland, and on Change history). */
export function ScheduledList({ domain, initial, empty }: { domain: string; initial: ScheduledChangeView[]; empty?: string }) {
  const [list, setList] = useState(initial);
  if (!list.length) return <p className="muted small">{empty ?? 'Nothing is scheduled.'}</p>;
  return (
    <ul className="sched-list plain-list">
      {list.map((s) => (
        <Row key={s.id} domain={domain} s={s} onChanged={(n) => setList((all) => all.map((x) => (x.id === n.id ? n : x)))} />
      ))}
    </ul>
  );
}

/** Change history's Scheduled card: shown when anything is waiting, or ran in the last 7 days. */
export function ScheduledCard({ domain, list, base }: { domain: string; list: ScheduledChangeView[]; base: string }) {
  if (!list.length) return null;
  const waiting = list.filter((s) => s.status === 'waiting' || s.status === 'running').length;
  return (
    <Card
      title={waiting ? `Scheduled · ${waiting} waiting` : 'Scheduled'}
      action={
        <Link className="link-btn" to={`${base}/leland`}>
          Ask Leland
        </Link>
      }
    >
      <div className="card-body">
        <p className="muted small">Changes Leland makes at a time you approved: later, or put back after a while. Each runs as the person who approved it, if they still can.</p>
        <ScheduledList domain={domain} initial={list} />
      </div>
    </Card>
  );
}
